import { KeyboardEvent, useState } from 'react';
import classNames from 'classnames';

import { icon_chevron_down, icon_chevron_up } from '@gn-audio/icons';

import { getTitle } from '../../../getters';
import useFallbackTranslation from '../../../utils/hooks/useFallbackTranslation';
import { BasketButton } from '../../Basket/BasketButton';
import { EcomOnly } from '../../EcomOnly/EcomOnly';
import { FeatureToggle } from '../../FeatureToggle';
import { JabraFocusGroup, JabraFocusItem } from '../../JabraFocus';
import { JabraIcon } from '../../JabraIcon/JabraIcon';
import { LogoLink } from '../../LogoLink/LogoLink';
import { UipMenu } from '../../UipMenu';
import { MenuItems } from '../enums/menu-items.enum';
import { menuComponentsMap } from '../headerComponentsMapper';
import { DISPLAY_VERSIONS } from '../MegaMenuConstants';
import { MenuItem, SubmenuId } from '../MegaMenuTypes';
import { getTopMenuItemNameFromSCMenuItem } from '../MegaMenuUtils';
import { useHeaderNavigation } from '../providers';

import styles from '../MegaMenu.module.css';

type Props = {
  isNavigationTransparent?: boolean;
  showNavigation?: boolean;
};

export const DesktopTopMenu = ({ showNavigation, isNavigationTransparent }: Props): JSX.Element => {
  const { t } = useFallbackTranslation();

  const [focusedItem, setFocusedItem] = useState<MenuItem>();
  const { menuItems, onMenuItemClick, closeMenu, activeItemId, openMenuItem } =
    useHeaderNavigation();

  const sideBarItems = [] as MenuItem[];
  const items = menuItems.filter((menuItem) => {
    if (menuItem.name !== MenuItems.SiteSelector && menuItem.name !== MenuItems.Search) {
      return menuItem;
    } else {
      sideBarItems.push(menuItem);
    }
    return;
  });

  const handleOpenMenuItem = (menuItem: MenuItem) => {
    openMenuItem && openMenuItem(menuItem);
  };

  const handleMenuKeydown = (e: KeyboardEvent) => {
    if (!focusedItem) {
      return;
    }
    if (e.key === 'ArrowDown') {
      if (activeItemId === getTopMenuItemNameFromSCMenuItem(focusedItem)) {
        return;
      }
      focusedItem && handleOpenMenuItem(focusedItem);
      e.preventDefault();
      e.stopPropagation();
    }
    if (e.key === 'ArrowUp') {
      closeMenu();
      e.preventDefault();
      e.stopPropagation();
    }
  };

  return (
    <JabraFocusGroup
      onKeyDown={handleMenuKeydown}
      orientation="horizontal"
      className="hidden w-full lg:block"
      id="top-menu"
    >
      <div
        className={`h-16 w-full grid-cols-3 items-center px-3 lg:grid min-[1930px]:px-0 ${
          DISPLAY_VERSIONS.DESKTOP
        } topMenu ${isNavigationTransparent ? 'bg-transparent' : 'bg-white'}`}
      >
        <div>{<LogoLink />}</div>

        {showNavigation ? (
          <>
            <nav
              aria-label={t('topmenu.TopMenu')}
              className="top-menu flex items-center justify-center"
            >
              <ul className="flex">
                {items.map((menuItem) => {
                  const isActive = activeItemId === getTopMenuItemNameFromSCMenuItem(menuItem);
                  return (
                    <li
                      onClick={() => onMenuItemClick?.(menuItem)}
                      className={`mx-2 box-border cursor-pointer text-nowrap px-1 text-[0.875rem] leading-6 text-black`}
                      key={menuItem.id}
                    >
                      <JabraFocusItem onFocus={() => closeMenu()}>
                        <button
                          onFocus={() => setFocusedItem(menuItem)}
                          onBlur={() => setFocusedItem(undefined)}
                          id={`group-12 ${menuItem.id}`}
                          className="group flex items-center"
                        >
                          <span
                            className={`border-b-0 bg-gradient-to-r from-yellow to-yellow bg-[length:0_2px] bg-left-bottom bg-no-repeat transition-[background-size] duration-500 ease-in-out group-hover:bg-[length:100%_2px] group-hover:bg-left-bottom ${
                              isActive && 'bg-[length:100%_2px] bg-left-bottom'
                            }`}
                          >
                            {getTitle(menuItem)}
                          </span>
                          <JabraIcon
                            className="ml-3 h-3 w-3"
                            Icon={isActive ? icon_chevron_up : icon_chevron_down}
                          />
                        </button>
                      </JabraFocusItem>
                    </li>
                  );
                })}
              </ul>
            </nav>

            <ul className="flex justify-self-end">
              {sideBarItems.map((menuItem) => {
                const isActiveSidebarItem =
                  activeItemId === getTopMenuItemNameFromSCMenuItem(menuItem);
                return (
                  <JabraFocusItem
                    as="li"
                    key={menuItem.id}
                    onFocus={() => {
                      if (menuItem.name === MenuItems.SiteSelector) setFocusedItem(menuItem);
                    }}
                  >
                    <div
                      onClick={() => onMenuItemClick?.(menuItem)}
                      className={styles.menuButton}
                      onBlur={() => setFocusedItem(undefined)}
                    >
                      {menuComponentsMap[menuItem.name as SubmenuId]?.(
                        () => onMenuItemClick?.(menuItem),
                        isActiveSidebarItem
                      ) || (
                        <button className="btn-top-menu">
                          <span className="menuItem pb-1">{getTitle(menuItem)}</span>
                          <span className="ml-1"></span>
                        </button>
                      )}
                    </div>
                  </JabraFocusItem>
                );
              })}

              <EcomOnly>
                <FeatureToggle featureName="TopMenuUipEnabled">
                  <JabraFocusItem
                    as="li"
                    key="uip-icon"
                    className={classNames('flex justify-center', styles.menuButton)}
                  >
                    <UipMenu />
                  </JabraFocusItem>
                </FeatureToggle>
                <li className={styles.menuButton} key="basket">
                  <JabraFocusItem asChild>
                    <BasketButton />
                  </JabraFocusItem>
                </li>
              </EcomOnly>
            </ul>
          </>
        ) : null}
      </div>
    </JabraFocusGroup>
  );
};
